import React from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Fade,
  useTheme,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  VerifiedUser as VerifiedUserIcon,
  Login as LoginIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";

interface SignupStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  status: "pending" | "active" | "completed";
  color: string;
}

const SignupProcessInfographic: React.FC = () => {
  const theme = useTheme();

  const steps: SignupStep[] = [
    {
      icon: <PersonAddIcon sx={{ fontSize: 32 }} />,
      title: "Fill Out Signup Form",
      description:
        "Enter your organization details, email, and create a secure password",
      status: "completed",
      color: theme.palette.primary.main,
    },
    {
      icon: <EmailIcon sx={{ fontSize: 32 }} />,
      title: "Verification Email Sent",
      description:
        "Check your inbox for a 6-digit verification code",
      status: "completed",
      color: theme.palette.colors.blue,
    },
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 32 }} />,
      title: "Verify Your Account",
      description:
        "Enter the code on the Verify Your Account screen to activate your organization account",
      status: "completed",
      color: theme.palette.colors.green,
    },
    {
      icon: <LoginIcon sx={{ fontSize: 32 }} />,
      title: "First Login",
      description: "Sign in with your credentials to access your dashboard",
      status: "active",
      color: theme.palette.colors.purple,
    },
    {
      icon: <HourglassIcon sx={{ fontSize: 32 }} />,
      title: "Account Pending Approval",
      description:
        "Your account is reviewed by our team for security and compliance",
      status: "pending",
      color: theme.palette.colors.orange,
    },
    {
      icon: <CheckCircleIcon sx={{ fontSize: 32 }} />,
      title: "Full Access Granted",
      description:
        "Once approved, you can create notices, manage users, and use all features",
      status: "pending",
      color: theme.palette.colors.green,
    },
  ];

  const getStepColor = (status: string, defaultColor: string) => {
    switch (status) {
      case "completed":
        return theme.palette.colors.green;
      case "active":
        return theme.palette.primary.main;
      case "pending":
        return theme.palette.colors.orange;
      default:
        return defaultColor;
    }
  };

  const getStepIcon = (step: SignupStep) => {
    const iconColor = getStepColor(step.status, step.color);
    const bgColor =
      step.status === "completed"
        ? theme.palette.colors.green
        : step.status === "active"
        ? theme.palette.primary.main
        : theme.palette.colors.orange;

    return (
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${bgColor} 0%, ${iconColor} 100%)`,
          color: "common.white",
          boxShadow: `0 8px 32px ${bgColor}40`,
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            top: -4,
            left: -4,
            right: -4,
            bottom: -4,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${bgColor}20, ${iconColor}20)`,
            zIndex: -1,
          },
        }}
      >
        {step.icon}
      </Box>
    );
  };

  return (
    <Box sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.colors.teal} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          Organization Signup Process
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "600px",
            mx: "auto",
            lineHeight: 1.6,
            fontWeight: 400,
          }}
        >
          Follow these simple steps to get your organization set up and running
          on Noticeboard
        </Typography>
      </Box>

      {/* Process Steps */}
      <Box sx={{ maxWidth: "1000px", mx: "auto" }}>
        {steps.map((step, index) => (
          <Fade in={true} timeout={800 + index * 200} key={index}>
            <Box sx={{ mb: 4 }}>
              <Card
                elevation={0}
                sx={{
                  border: `2px solid ${getStepColor(
                    step.status,
                    step.color
                  )}20`,
                  borderRadius: 4,
                  background:
                    step.status === "active"
                      ? `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.colors.teal}08 100%)`
                      : "white",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 12px 40px ${getStepColor(
                      step.status,
                      step.color
                    )}20`,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                    {/* Step Icon */}
                    {getStepIcon(step)}

                    {/* Step Content */}
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: getStepColor(step.status, step.color),
                          }}
                        >
                          {step.title}
                        </Typography>
                        {step.status === "completed" && (
                          <CheckCircleIcon
                            sx={{
                              color: theme.palette.colors.green,
                              fontSize: 24,
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                          fontSize: theme.typography.h5.fontSize,
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>

                    {/* Step Number */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${getStepColor(
                          step.status,
                          step.color
                        )} 0%, ${step.color} 100%)`,
                        color: "common.white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: theme.typography.h5.fontSize,
                        boxShadow: `0 4px 16px ${getStepColor(
                          step.status,
                          step.color
                        )}40`,
                      }}
                    >
                      {index + 1}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Arrow between steps */}
              {index < steps.length - 1 && (
                <Box sx={{ textAlign: "center", my: 2 }}>
                  <ArrowForwardIcon
                    sx={{
                      fontSize: 32,
                      color: theme.palette.colors.orange,
                      transform: "rotate(90deg)",
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 0.6 },
                        "50%": { opacity: 1 },
                        "100%": { opacity: 0.6 },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Fade>
        ))}
      </Box>

      {/* Summary Card */}
      <Fade in={true} timeout={1200}>
        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.colors.teal} 100%)`,
            borderRadius: 4,
            p: 4,
            mt: 6,
            textAlign: "center",
            color: "common.white",
            maxWidth: "800px",
            mx: "auto",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            🎉 You're Almost There!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              opacity: 0.9,
              lineHeight: 1.6,
              fontSize: theme.typography.h5.fontSize,
            }}
          >
            Most organizations are approved within 24 hours. Our team will
            review your organization details and get you set up quickly!
          </Typography>
        </Paper>
      </Fade>
    </Box>
  );
};

export default SignupProcessInfographic;
